<template>
  <div
    class="sticker"
    :style="{
      width: widthMm + 'mm',
      height: heightMm + 'mm',
      paddingTop: parameters.top + 'px',
      paddingRight: parameters.right + 'px',
      paddingBottom: parameters.bottom + 'px',
      paddingLeft: Number(parameters.left || 0) + leadingPadPx + 'px',
      fontSize: effectiveFontSize + 'pt',
      '--footer-scale': footerScale,
    }"
  >
    <div
      :class="companyName.startsWith('Bergsoe') ? 'bg-img' : 'panya-bg-img'"
      :style="watermarkStyle"
    ></div>
    <div class="cell-content">
      <div
        :style="`font-size: ${scaledHeaderPt}pt`"
        class="font-weight-bold"
      >
        {{ companyName }}
      </div>
      <div class="qr-row flex-grow-1">
        <!-- Pre-rendered <img>, absolutely positioned with a fixed box:
             flex/canvas sizing is unreliable in the print rasterizer -->
        <img
          v-if="qrDataUrl"
          class="qr-img"
          :src="qrDataUrl"
          :style="{
            width: scaledQrSize + 'px',
            height: scaledQrSize + 'px',
          }"
        />
        <div
          class="info-col"
          :style="{ marginLeft: scaledQrSize + 12 + 'px' }"
        >
          <table class="qr-info">
            <tbody>
              <tr v-if="parameters.brandName">
                <td class="font-weight-light label-col">Brand</td>
                <td>{{ parameters.brandName }}</td>
              </tr>
              <tr v-if="commodityName">
                <td class="font-weight-light label-col">Commodity</td>
                <td>{{ commodityName }}</td>
              </tr>
              <tr v-if="worksheet.documentNumber">
                <td class="font-weight-light label-col">Lot No</td>
                <td>{{ worksheet.documentNumber }}-{{ lotIndex }}</td>
              </tr>
              <tr v-if="entry.netWeight != null">
                <td class="font-weight-light label-col">Net Weight</td>
                <td>
                  {{
                    (entry.netWeight + (entry.netAdjustmentWeight || 0))
                      | formatNumber
                  }}
                  {{ productInfo ? productInfo.measureWeight : "" }}
                </td>
              </tr>
              <tr
                v-for="(field, index) in parameters.optionalFields"
                :key="index"
              >
                <td class="font-weight-light label-col">{{ field.label }}</td>
                <td>{{ field.value }}</td>
              </tr>
              <tr>
                <td class="font-weight-light label-col">Date</td>
                <td>{{ labelDate | formatDateYear }}</td>
              </tr>
              <tr>
                <td class="font-weight-light label-col">Origin</td>
                <td>Thailand</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div
        ref="footer"
        class="ma-0 pa-0 font-weight-light footer-text"
        :style="`font-size: ${scaledFooterPt}pt;`"
        v-if="companyAddress"
      >
        {{ companyAddress }} Tel: {{ companyPhone }}
        <div
          class="text-caption font-weight-light font-size-small d-print-none"
          style="float: right; position: relative"
        >
          {{ lotIndex }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { labelSizeScale } from "@/utils/labelDeviceConfig.js";

/**
 * One printable label. Rendered by the batch print page for every entry, and
 * off-screen by auto-print for a single just-saved entry — both go through the
 * same component so a label printed at the bench and one reprinted later are
 * identical.
 *
 * The lot index is a PROP, not computed here: it is the entry's position in the
 * worksheet's entry list, only the owner of that list can say what it is, and on
 * LME products it is encoded into the QR — a wrong one produces a scannable but
 * wrong sticker.
 */
export default {
  name: "LabelSticker",
  props: {
    entry: { type: Object, required: true },
    worksheet: { type: Object, required: true },
    parameters: { type: Object, required: true },
    qrDataUrl: { type: String, default: "" },
    lotIndex: { type: [Number, String], default: "" },
    commodityName: { type: String, default: "" },
    productInfo: { type: Object, default: null },
    companyName: { type: String, default: "" },
    companyAddress: { type: String, default: "" },
    companyPhone: { type: String, default: "" },
  },
  data() {
    return {
      footerScale: 1,
    };
  },
  computed: {
    widthMm() {
      return Number(this.parameters.labelWidth) || 90;
    },
    heightMm() {
      return Number(this.parameters.labelHeight) || 50;
    },
    // The date the bundle was WEIGHED, not the worksheet's process date.
    //
    // Two reasons. The process date does not exist until the worksheet is
    // submitted, so a label printed while weighing showed "n/a". And revising a
    // worksheet moves that date, which would make a reprint disagree with the
    // sticker already on the goods — the weighing date is the fact the label is
    // actually recording, and it does not move.
    labelDate() {
      return (
        this.entry.createTime ||
        this.entry.updateTime ||
        this.worksheet.processTime ||
        null
      );
    },
    // Everything on the label is sized for the 90x50 roll: the QR is a pixel
    // count and the font has a fixed ceiling. On the 100x150 roll that left the
    // same small sticker adrift in twice the space. Scale it all by how much
    // bigger the label is, so a larger roll gives a larger sticker rather than
    // more white paper. 90x50 is the baseline and comes out unchanged.
    sizeScale() {
      return labelSizeScale(this.parameters);
    },
    // Was a blank strip to keep the QR out of the faint leading edge on rotated
    // labels. That faintness turned out to be the printer shipping at density 0,
    // fixed properly since — while this quietly went on stealing 45px of width
    // from the 100x150 layout, which is scaled up 1.67x and has none to spare.
    // The columns collided and the last rows fell off the label.
    leadingPadPx() {
      return 0;
    },
    scaledQrSize() {
      return Math.round((Number(this.parameters.qrSize) || 100) * this.sizeScale);
    },
    scaledHeaderPt() {
      return (
        Math.round((Number(this.parameters.headerFontSize) || 12) * this.sizeScale * 10) / 10
      );
    },
    scaledFooterPt() {
      return (
        Math.round((Number(this.parameters.footerFontSize) || 8) * this.sizeScale * 10) / 10
      );
    },
    // Keep the watermark small and behind the text area only — to the
    // right of the QR, vertically centered within the info block
    watermarkStyle() {
      const qrSize = this.scaledQrSize;
      const left = Number(this.parameters.left) || 0;
      return {
        left: `${left + qrSize + 12}px`,
        right: "8px",
        top: "18%",
        bottom: "18%",
      };
    },
    // Auto-shrink the row font so extra lines (brand, optional fields)
    // still fit the fixed label height instead of overflowing it
    effectiveFontSize() {
      const base = (Number(this.parameters.fontSize) || 13) * this.sizeScale;
      const rowCount =
        5 + // Commodity, Lot No, Net Weight, Date, Origin
        (this.parameters.brandName ? 1 : 0) +
        (this.parameters.optionalFields
          ? this.parameters.optionalFields.length
          : 0);

      const mmToPx = 96 / 25.4;
      const ptToPx = 96 / 72;
      const labelHeightPx = this.heightMm * mmToPx;
      const paddingPx =
        (Number(this.parameters.top) || 0) +
        (Number(this.parameters.bottom) || 0);
      const headerPx = this.scaledHeaderPt * ptToPx * 1.2;
      const footerPx = this.scaledFooterPt * ptToPx * 1.2 + 4;
      const availablePx = labelHeightPx - paddingPx - headerPx - footerPx;

      // row height = font px * line-height 1.3
      const maxPt = Math.floor(availablePx / rowCount / 1.3 / ptToPx);

      // Fit the WIDTH too. The rows sit beside the QR, and how much room is
      // left depends on the QR size and padding a station has stored — so a
      // font chosen only against the height can still be too wide, wrap, and
      // cost the last rows. Estimated from the widest row at ~0.55em per
      // character, which is close enough for a table of short values.
      const widthPx =
        this.widthMm * mmToPx -
        (Number(this.parameters.left) || 0) -
        (Number(this.parameters.right) || 0) -
        this.scaledQrSize -
        12;
      const widest = this.rows.reduce(
        (max, r) => Math.max(max, `${r.label}   ${r.value}`.length),
        0
      );
      const widthCapPt = widest
        ? Math.floor(widthPx / (widest * 0.55) / ptToPx)
        : maxPt;

      return Math.max(6, Math.min(base, maxPt, widthCapPt));
    },
  },
  methods: {
    // Address + phone must stay one line. nowrap makes the footer wider
    // than its column on a narrow label, so scale it down to fit.
    fitFooter() {
      const el = this.$refs.footer;
      if (!el || !el.parentElement) {
        return;
      }
      const available = el.parentElement.clientWidth;
      const natural = el.scrollWidth;
      const scale =
        natural > available && natural > 0
          ? Math.max(0.5, available / natural)
          : 1;
      // guard against an update loop (scale change -> updated() -> ...)
      if (Math.abs(scale - this.footerScale) > 0.01) {
        this.footerScale = scale;
      }
    },
  },
  mounted() {
    this.fitFooter();
  },
  updated() {
    this.fitFooter();
  },
};
</script>

<style scoped>
table.qr-info {
  width: 100%;
}

/* Tight line-height so header + 5 rows + footer + QR genuinely fit the
   label height — overflow makes the print engine squash the QR */
table.qr-info td {
  line-height: 1.3;
}

table.qr-info > tbody > tr > td:nth-child(1) {
  padding-right: 5px;
  white-space: nowrap;
}

/* Values must not wrap either. A wrapped value makes its row twice as tall,
   which pushes the last rows and the footer off the bottom of the label — the
   sticker still prints, just without Origin or the address on it. Shrinking to
   fit is handled in effectiveFontSize; this stops a near-miss from silently
   costing a row. */
table.qr-info > tbody > tr > td:nth-child(2) {
  white-space: nowrap;
}

/* Field labels scale with the (auto-shrunk) row font instead of
   Vuetify's fixed 12px text-caption */
td.label-col {
  font-size: 0.85em;
}

div.cell-content > .font-weight-bold {
  line-height: 1.2;
}

div.sticker {
  position: relative;
  overflow: hidden;
  border: 1px dashed black;
  margin: 0 auto 10px auto;
  box-sizing: border-box;
}

/* Watermark scaled to the label so it always sits inside the sticker */
div.bg-img {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.15;
  background-size: contain;
  background-repeat: no-repeat;
  background-image: url("~@/assets/logo.png");
  background-position: center;
}

div.panya-bg-img {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.35;
  background-size: contain;
  background-repeat: no-repeat;
  background-image: url("~@/assets/panya-logo.png");
  background-position: center;
}

/* Fill the full label: content column stretches to label height,
   QR + info row centers vertically, footer pins to the bottom */
div.cell-content {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
}

/* QR fixed-box on the left, vertically centered; info column beside it.
   Absolute positioning because flex sizing of images is unreliable in
   the print rasterizer (squashed/shrunken QR = unscannable) */
div.qr-row {
  position: relative;
}

img.qr-img {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

div.info-col {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
}

/* Keep the address + phone on a single line. The print area can be
   narrower than the label, so the footer is scaled down to fit its
   width instead of wrapping to a second line */
.footer-text {
  line-height: 1.2;
  white-space: nowrap;
  transform: scale(var(--footer-scale, 1));
  transform-origin: left center;
}

@media print {
  div.sticker {
    position: relative;
    border: 0px;
    margin: 0;

    -webkit-print-color-adjust: exact !important; /* Chrome, Safari, Edge */
    color-adjust: exact !important; /*Firefox*/
  }
}
</style>
